import { Badge, Card, CardContent } from '@1trading/react';
import { useMemo } from 'react';
import { MetricTile } from '@/components/ui/metric-tile';
import { formatEuros } from '@/utils/format';
import { computeReportOverview } from './report-overview';

const ASSET_TOTAL_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4 });
const COUNT_FORMAT = new Intl.NumberFormat('en-GB');

export interface ReportOverviewCardsProps {
  headers: string[];
  /** The currently-filtered rows — the cards always describe what the table shows. */
  rows: string[][];
  totalRowCount: number;
}

// Tailwind needs literal class names, so the fill-the-row span is a lookup on the
// number of stat cards already occupying the currencies card's row (mod 4 columns).
const CURRENCIES_CARD_SPAN: Record<number, string> = {
  0: 'sm:col-span-2 lg:col-span-4',
  1: 'sm:col-span-2 lg:col-span-3',
  2: 'sm:col-span-2 lg:col-span-2',
  3: 'sm:col-span-2 lg:col-span-1',
};

/** Aggregate cards over the filtered audit rows: user count, EUR holdings, venue funds, per-currency breakdown. */
export const ReportOverviewCards = ({ headers, rows, totalRowCount }: ReportOverviewCardsProps) => {
  const overview = useMemo(() => computeReportOverview(headers, rows), [headers, rows]);
  if (!overview) return null;

  const { userCount, totalEur, otmTotalEur, oteFiatEur, oteCryptoEur, currencies } = overview;
  const isFiltered = userCount !== totalRowCount;

  const statCards = [
    { label: 'Users', value: COUNT_FORMAT.format(userCount), hint: isFiltered ? `of ${COUNT_FORMAT.format(totalRowCount)} total` : undefined },
    ...(totalEur !== null ? [{ label: 'Total Holdings', value: formatEuros(totalEur) }] : []),
    ...(otmTotalEur !== null ? [{ label: 'OTM Funds Held', value: formatEuros(otmTotalEur) }] : []),
    ...(oteFiatEur !== null ? [{ label: 'OTE Fiat Funds Held', value: formatEuros(oteFiatEur) }] : []),
    ...(oteCryptoEur !== null ? [{ label: 'OTE Crypto Funds Held', value: formatEuros(oteCryptoEur) }] : []),
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {statCards.map(({ label, value, hint }) => (
        <MetricTile hint={hint} key={label} label={label} value={value} />
      ))}
      {currencies.length > 0 ? (
        <Card className={CURRENCIES_CARD_SPAN[statCards.length % 4]}>
          <CardContent className="flex flex-col gap-2 pt-4 pb-4">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">By Currency</span>
            <div className="flex max-h-20 flex-wrap content-start gap-1 overflow-y-auto">
              {currencies.map(({ coin, total, holders }) => (
                <Badge className="gap-1 font-mono text-[11px]" key={coin} title={`${holders} holder${holders === 1 ? '' : 's'}`}>
                  <span className="font-semibold">{coin}</span>
                  <span className="tabular-nums">{ASSET_TOTAL_FORMAT.format(total)}</span>
                  <span className="text-muted-foreground tabular-nums">·{holders}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
ReportOverviewCards.displayName = 'ReportOverviewCards';
